import { supabase } from '@/integrations/supabase/client';
import { checkTableExists, safeApiRequest, safeProfileUpdate } from '@/utils/api-helpers';

// Define a type for valid subscription statuses
type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trialing' | 'trial';

// Paths that don't require an active subscription to access
export const PUBLIC_PATHS = [
  '/',
  '/login',
  '/signup',
  '/reset-password',
  '/subscription/plans',
  '/subscription/success',
  '/subscription/cancel'
];

// Paths that require subscription for creating/editing data
// but allow read-only access without subscription
export const READ_ONLY_PATHS = [
  // No read-only paths - all paths now require subscription
];

// Helper function to determine if a status is valid/active
const isActiveStatus = (status: string | null): boolean => {
  if (!status) return false;
  return ['active', 'trialing', 'trial'].includes(status);
};

// Helper function to check if subscription is valid based on end date
const isValidSubscription = (endDate: string | null): boolean => {
  if (!endDate) return false;
  
  try {
    const subscriptionEnd = new Date(endDate);
    const now = new Date();
    return subscriptionEnd > now;
  } catch (e) {
    console.error('Error checking subscription end date:', e);
    return false;
  }
};

// Helper to update user profile subscription status
export const updateProfileSubscriptionStatus = async (userId: string, status: string | null, endDate: string | null): Promise<boolean> => {
  if (!userId) return false;
  
  // Use the safeProfileUpdate helper which handles different schema versions
  return safeProfileUpdate(userId, {
    subscription_status: status || 'canceled',
    subscription_end_date: endDate || null
  });
};

// Force re-verification of subscription status for a user
export const forceVerifySubscription = async (userId: string): Promise<boolean> => {
  if (!userId) return false;
  
  // IMPORTANT: Always clear cached subscription status to ensure fresh verification
  localStorage.removeItem(`subscription_status_${userId}`);
  localStorage.removeItem(`subscription_check_time_${userId}`);
  
  // Check if we should skip immediate verification during app initialization
  if (localStorage.getItem('skip_immediate_verification') === 'true') {
    console.log('Skipping immediate subscription verification during app initialization');
    return true; // Return true to avoid blocking the UI
  }
  
  console.log('Running force verification for user:', userId);
  
  try {
    // First, check if this is a special admin user by email
    const { data: userData } = await supabase.auth.getUser();
    const specialEmails = ['simmakin@gmail.com', 'simmakin@googlemail.com', 'test@coachvault.co.uk'];
    
    if (userData?.user?.email && specialEmails.includes(userData.user.email.toLowerCase())) {
      console.log('Special admin user detected, granting access');
      // Update the profile to ensure admin status is reflected
      await safeProfileUpdate(userId, {
        subscription_status: 'active',
        subscription_end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() // 1 year from now
      });
      return true;
    }
    
    // Get the user's profile to check if they're an admin
    const { data: profileData, error: profileError } = await safeApiRequest(
      () => supabase
        .from('profiles')
        .select('role, subscription_status, subscription_end_date')
        .eq('id', userId)
        .single(),
      null
    );
    
    if (profileError) {
      console.log('Error fetching profile during force verification, continuing with checks');
    } else if (profileData?.role === 'admin') {
      // Admin users always have access, ensure their profile reflects this
      await safeProfileUpdate(userId, {
        subscription_status: 'active',
        subscription_end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() // 1 year from now
      });
      
      // Clear any cached negative subscription status
      localStorage.removeItem(`subscription_status_${userId}`);
      localStorage.removeItem(`subscription_check_time_${userId}`);
      
      return true;
    }
    
    // Next check actual subscription data from the subscriptions table
    // First check if the table exists to avoid 404 errors
    const subscriptionsTableExists = await checkTableExists('subscriptions');
    
    if (!subscriptionsTableExists) {
      console.log('Subscriptions table does not exist, skipping subscription check');
      // Update profile to indicate no subscription since we can't verify
      await safeProfileUpdate(userId, {
        subscription_status: 'canceled',
        subscription_end_date: null
      });
      return false;
    }
    
    // Table exists, proceed with checking subscription data
    const { data: subscriptionData, error: subscriptionError } = await safeApiRequest(
      () => supabase
        .from('subscriptions')
        .select('id, status, current_period_end, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1),
      []
    );
      
    if (subscriptionError) {
      console.log('Error fetching subscription data during force verification');
      return false;
    } else if (subscriptionData && subscriptionData.length > 0) {
      const subscription = subscriptionData[0];
      
      // Check if subscription is active and valid
      const isValid = isActiveStatus(subscription.status) && 
                    isValidSubscription(subscription.current_period_end);
                    
      // Always update profile with latest subscription data
      await updateProfileSubscriptionStatus(
        userId,
        subscription.status,
        subscription.current_period_end
      );
      
      // Update local storage cache
      if (isValid) {
        localStorage.removeItem(`subscription_status_${userId}`);
        localStorage.removeItem(`subscription_check_time_${userId}`);
        
        console.log('Force verification found valid subscription:', subscription.id);
        return true;
      } else {
        // Subscription found but not valid/active
        localStorage.setItem(`subscription_status_${userId}`, 'false');
        localStorage.setItem(`subscription_check_time_${userId}`, Date.now().toString());
        
        console.log('Force verification found invalid subscription:', subscription.id);
      }
    } else {
      // No subscription record found at all
      console.log('No subscription found during force verification');
      
      // Update profile to explicitly mark no subscription
      await updateProfileSubscriptionStatus(userId, 'canceled', null);
      
      // Update cache
      localStorage.setItem(`subscription_status_${userId}`, 'false');
      localStorage.setItem(`subscription_check_time_${userId}`, Date.now().toString());
    }
    
    // Try to check the checkout_sessions table to see if there's a recent pending session
    // This is optional and may not exist in all environments, so wrap in try/catch
    try {
      // First check if the table exists to avoid 404 errors
      const checkoutTableExists = await checkTableExists('checkout_sessions');
      
      if (checkoutTableExists) {
        // Table exists, check for recent checkout sessions
        const { data: checkoutSessions } = await safeApiRequest(
          () => supabase
            .from('checkout_sessions')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(1),
          []
        );
          
        if (checkoutSessions && checkoutSessions.length > 0) {
          const session = checkoutSessions[0];
          const sessionTime = new Date(session.created_at).getTime();
          const now = Date.now();
          
          // If session is less than 1 hour old, consider it potentially active
          if (now - sessionTime < 60 * 60 * 1000) {
            console.log('Recent checkout session found, may be pending:', session.id);
            return true;
          }
        }
      } else {
        console.log('Skipping checkout sessions check - table does not exist');
      }
    } catch (error) {
      // Silently handle errors - this is an optional check
      console.log('Error checking checkout sessions - continuing with verification');
      console.log('Skipping checkout sessions check - table may not exist');
    }
    
    // Try to log the verification in the subscription_verifications table
    try {
      // Check if the table exists first
      const verificationsTableExists = await checkTableExists('subscription_verifications');
        
      // Only proceed if the table exists
      if (verificationsTableExists) {
        await safeApiRequest(
          () => supabase.from('subscription_verifications').insert({
            user_id: userId,
            verified_at: new Date().toISOString(),
            result: 'none_found',
            notes: 'Force verification found no active subscription'
          }),
          null
        );
      } else {
        console.log('subscription_verifications table does not exist, skipping logging');
      }
    } catch (logError) {
      // Non-critical, just log the error
      console.log('Error logging subscription verification - non-critical');
    }
    
    return false;
  } catch (error) {
    console.error('Error in forceVerifySubscription:', error);
    return false;
  }
};

// Check if a user has an active subscription
export const checkSubscriptionStatus = async (userId: string): Promise<boolean> => {
  if (!userId) return false;
  
  try {
    // First, check if this is a special admin or test user by email
    const { data: userData } = await supabase.auth.getUser();
    const specialEmails = ['simmakin@gmail.com', 'simmakin@googlemail.com', 'test@coachvault.co.uk'];
    
    if (userData?.user?.email && specialEmails.includes(userData.user.email.toLowerCase())) {
      console.log('Special user detected, granting access');
      
      // Update profile to ensure they have access
      await safeProfileUpdate(userId, {
        subscription_status: 'active',
        subscription_end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() // 1 year from now
      });
      
      // Mark as subscribed in local storage
      localStorage.setItem(`subscription_status_${userId}`, 'true');
      localStorage.setItem(`subscription_check_time_${userId}`, Date.now().toString());
      
      return true;
    }
    
    // Check cached status but only for positive results (active subscriptions)
    const cachedStatus = localStorage.getItem(`subscription_status_${userId}`);
    const cachedTimeStr = localStorage.getItem(`subscription_check_time_${userId}`);
    const appInitialLoad = localStorage.getItem('app_initial_load') === 'true';
    
    if (appInitialLoad && cachedStatus === 'true' && cachedTimeStr) {
      const cachedTime = parseInt(cachedTimeStr);
      const now = Date.now();
      
      // Trust cached positive result for 30 minutes during initial load
      if (now - cachedTime < 30 * 60 * 1000) {
        console.log('Using cached positive subscription status during initial load');
        return true;
      }
    }
    
    // First check for admin role in profiles
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('role, subscription_status, subscription_end_date, stripe_subscription_id')
      .eq('id', userId)
      .single();
    
    if (!profileError && profileData) {
      // Admin users always have access
      if (profileData.role === 'admin') {
        console.log('Admin user detected, granting access');
        localStorage.setItem(`subscription_status_${userId}`, 'true');
        localStorage.setItem(`subscription_check_time_${userId}`, Date.now().toString());
        return true;
      }
      
      // If profile has a Stripe subscription ID and active status, consider it valid
      if (profileData.stripe_subscription_id && isActiveStatus(profileData.subscription_status)) {
        console.log('Valid subscription found in profile', {
          status: profileData.subscription_status,
          stripeId: profileData.stripe_subscription_id
        });
        
        const isValid = isValidSubscription(profileData.subscription_end_date);
        if (isValid) {
          localStorage.setItem(`subscription_status_${userId}`, 'true');
          localStorage.setItem(`subscription_check_time_${userId}`, Date.now().toString());
          return true;
        }
      }
    }
    
    // Next, check the subscriptions table directly (most reliable source)
    const { data: subData, error: subError } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1);
    
    if (!subError && subData && subData.length > 0) {
      const subscription = subData[0];
      console.log('Found subscription record:', {
        status: subscription.status,
        stripeId: subscription.stripe_subscription_id
      });
      
      // If subscription exists and has active status
      if (isActiveStatus(subscription.status)) {
        const isValid = isValidSubscription(subscription.current_period_end);
        
        // Update profile with current subscription status
        await updateProfileSubscriptionStatus(
          userId,
          subscription.status,
          subscription.current_period_end
        );
        
        if (isValid) {
          localStorage.setItem(`subscription_status_${userId}`, 'true');
          localStorage.setItem(`subscription_check_time_${userId}`, Date.now().toString());
          return true;
        }
      }
    }
    
    // As a last resort, force verification from Stripe
    return await forceVerifySubscription(userId);
  } catch (error) {
    console.error('Error in checkSubscriptionStatus:', error);
    return false;
  }
};

// Check if a user can create or modify data in a specific section
export const canWriteData = async (userId: string, path: string): Promise<boolean> => {
  // Always allow access to public paths
  if (PUBLIC_PATHS.some(p => path === p || path.startsWith(p + '/'))) {
    return true;
  }
  
  // For all other paths, require an active subscription for write access
  return await checkSubscriptionStatus(userId);
};

// Check if a user can view a specific page
export const canAccessPath = async (userId: string, path: string): Promise<boolean> => {
  // Always allow access to public paths
  if (PUBLIC_PATHS.some(p => path === p || path.startsWith(p + '/'))) {
    return true;
  }
  
  // For read-only paths, always allow access for viewing
  if (READ_ONLY_PATHS.some(p => path === p || path.startsWith(p + '/'))) {
    return true;
  }
  
  // For all other paths, check subscription
  return await checkSubscriptionStatus(userId);
};
